import type { SelectedValue } from './types';

const findBack = <T extends HTMLElement>(
  pred: (elem: T) => boolean,
  elements: T[],
  fromIndex?: number,
) => {
  const start = fromIndex ?? elements.length - 1;
  for (let i = start; i >= 0; i--) {
    if (pred(elements[i])) {
      return elements[i];
    }
  }

  return undefined;
};

const findForward = <T extends HTMLElement>(
  pred: (elem: T) => boolean,
  elements: T[],
  fromIndex?: number,
) => {
  const start = fromIndex ?? 0;
  for (let i = start; i <= elements.length - 1; i++) {
    if (pred(elements[i])) {
      return elements[i];
    }
  }

  return undefined;
};

const isSelectable = <T extends HTMLElement>(elem: T) =>
  !elem.dataset.disabled || elem.dataset.disabled !== 'true';

export const findNext = <T extends HTMLElement>(elements: T[], current: T) => {
  const currentIndex = elements.findIndex((elem) => elem === current);

  if (currentIndex === elements.length - 1) {
    return undefined;
  }

  return findForward(isSelectable, elements, currentIndex + 1);
};

export const findPrevious = <T extends HTMLElement>(elements: T[], current: T) => {
  const currentIndex = elements.findIndex((elem) => elem === current);

  if (currentIndex === 0) {
    return undefined;
  }

  return findBack(isSelectable, elements, currentIndex - 1);
};

type KeyboardKeys = 'ArrowUp' | 'ArrowDown' | 'Space' | 'Enter' | 'Escape' | 'Tab';
type Handler = <T extends HTMLElement = HTMLElement>(elements: T[], currentElement: T) => void;

export const createKeyDownHandler =
  (events: Record<KeyboardKeys, Handler>) =>
  <T extends HTMLElement>(event: KeyboardEvent) => {
    const currentElement = event.currentTarget as T;
    const siblingNodes = currentElement.parentElement?.children;

    const siblings = (siblingNodes ? Array.from(siblingNodes) : []) as T[];
    const key = event.key === ' ' ? 'Space' : (event.key as KeyboardKeys);
    const handler = events[key];

    if (handler && event.key === 'Tab') {
      handler(siblings, currentElement);
      return;
    }

    if (handler) {
      event.preventDefault();
      handler(siblings, currentElement);
    }
  };

export const focusNext: Handler = <T extends HTMLElement>(elements: T[], currentElement: T) => {
  const nextElement = findNext(elements, currentElement);
  nextElement?.focus();
};

export const focusPrevious: Handler = <T extends HTMLElement>(elements: T[], currentElement: T) => {
  const previousElement = findPrevious(elements, currentElement);
  previousElement?.focus();
};

export const focusFirstOrSelected = <T extends HTMLElement>(menuElement: T) => {
  const selected = menuElement.querySelector('[aria-selected=true]') as T;

  if (selected) {
    return selected.focus();
  }

  const elem = menuElement.querySelector(':not([aria-disabled=true])') as HTMLElement;
  elem?.focus();
};

export const select =
  (onSelect: <T extends HTMLElement>(item: T) => void) =>
  <T extends HTMLElement>(_: T[], currentElement: T) => {
    onSelect(currentElement);
  };

export const findOption = <T extends HTMLElement>(
  menu: T | null,
  value: string,
): SelectedValue | undefined => {
  if (!menu) {
    return undefined;
  }

  const selectedOption = menu.querySelector(`[data-value=${value}]`) as HTMLElement | null;

  if (!selectedOption) {
    return undefined;
  }

  return {
    label: selectedOption.dataset.label as string,
    value: selectedOption.dataset.value as string,
  };
};
